import { HoneycombWebSDK } from '@honeycombio/opentelemetry-web'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { B3InjectEncoding, B3Propagator } from '@opentelemetry/propagator-b3'
// eslint-disable-next-line boundaries/no-unknown
import { router } from 'App'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { useEffect } from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { RouteContextSpanProcessor } from './routeContextSpanProcessor'
import { ATTR_SERVICE_VERSION } from './spanAttributes'
import {
  UserContext,
  UserContextSpanProcessor,
} from './userContextSpanProcessor'

// Regex patterns for backend services that should receive trace context headers
// Matches Gecko backend domains: *.geckorobotics.com, *.cloud.geckorobotics.com, *.cantilever.app, *.geckorobotics.dev
const TRACE_PROPAGATION_URLS = [
  /https:\/\/.*\.geckorobotics\.com/,
  /https:\/\/.*\.geckorobotics\.dev/,
  /https:\/\/.*\.cantilever\.app/,
]

/**
 * Module-level singleton state for the Honeycomb SDK.
 * OpenTelemetry global APIs (trace, propagation, context, metrics) can only
 * be registered ONCE per page load. Attempting to reinitialize the SDK
 * causes "duplicate registration" errors.
 */
let userContextProcessor: UserContextSpanProcessor | null = null
let isInitialized = false

/**
 * Initialize the Honeycomb SDK. This should only be called once per page load.
 * Subsequent calls will be ignored and return the existing processors.
 *
 * @param endpoint - The OTEL collector URL
 * @param serviceVersion - The application version
 * @param authorizationHeader - The Authorization header value for authenticating with the collector
 * @returns Object containing the user context processor for updating user info
 */
export function initializeHoneycombSdk(
  endpoint: string,
  serviceVersion: string,
  authorizationHeader: string
) {
  // Return existing processor if already initialized
  if (isInitialized && userContextProcessor) {
    return
  }

  // Create the user context processor that can be updated dynamically
  userContextProcessor = new UserContextSpanProcessor()

  // Use only B3 propagation to completely bypass GCP load balancer's
  // automatic traceparent header injection/modification.
  const propagator = new B3Propagator({
    injectEncoding: B3InjectEncoding.MULTI_HEADER,
  })

  const sdk = new HoneycombWebSDK({
    endpoint,
    serviceName: 'forecast',
    skipOptionsValidation: true, // because we are not including apiKey
    // Authorization header for authenticating with the OTEL collector
    headers: {
      Authorization: authorizationHeader,
    },
    // Static resource attributes - user info is added dynamically via UserContextSpanProcessor
    resourceAttributes: {
      [ATTR_SERVICE_VERSION]: serviceVersion,
    },
    // Span processors for dynamic context enrichment
    spanProcessors: [
      new RouteContextSpanProcessor(router),
      userContextProcessor,
    ],
    textMapPropagator: propagator,
    // Enable automatic instrumentation with trace propagation to backend services
    // See: https://docs.honeycomb.io/send-data/javascript-browser/#propagating-span-context
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URLS,
          ignoreNetworkEvents: true,
        },
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URLS,
          ignoreNetworkEvents: true,
        },
      }),
    ],
  })

  sdk.start()
  isInitialized = true
}

/**
 * Update the user context on the existing SDK instance.
 * This allows user info to change (e.g., after login) without reinitializing the SDK.
 */
export function updateUserContext(context: UserContext | null): void {
  if (userContextProcessor) {
    userContextProcessor.setUserContext(context)
  }
}

/**
 * Check if the SDK has been initialized.
 */
export function isHoneycombInitialized(): boolean {
  return isInitialized
}

/**
 * Ensure Honeycomb SDK is initialized
 */
export function useHoneycombSdk(): void {
  const { authorization } = useGeckoAuth()
  const { user, hasPermissions } = useGeckoPermissions()
  const { OTEL_COLLECTOR_URL, FORECAST_VERSION } = useRuntimeVariables()

  const isEnabled = !!OTEL_COLLECTOR_URL

  // Initialize SDK once when we have auth (if enabled and not already initialized)
  useEffect(() => {
    if (!isEnabled) {
      return
    }

    // Wait for auth token before initializing
    if (!authorization) {
      return
    }

    // Only initialize if not already done
    if (!isHoneycombInitialized()) {
      initializeHoneycombSdk(
        OTEL_COLLECTOR_URL,
        FORECAST_VERSION,
        authorization
      )
    }
    // Note: We intentionally don't return a cleanup function that shuts down
    // the SDK, because OpenTelemetry globals can't be re-registered after shutdown.
    // The SDK will persist for the lifetime of the page.
  }, [isEnabled, OTEL_COLLECTOR_URL, FORECAST_VERSION, authorization])

  // Update user context when user info changes
  useEffect(() => {
    if (!isEnabled || !isHoneycombInitialized()) {
      return
    }

    if (user?.id) {
      const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
      updateUserContext({
        userId: user.id,
        userEmail: user.email ?? '',
        isInternalUser,
      })
    } else {
      // Clear user context when logged out
      updateUserContext(null)
    }
  }, [isEnabled, user?.id, user?.email, hasPermissions])
}
